import { Typography } from '@euler/app/components/typography';
import { useAiChatAttached } from '@euler/app/flows/ai-suite/chat/AiChatActions';
import { SecondaryButton } from '@euler/app/flows/ai-suite/components/PrimaryButton';
import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { BiDraftDetail } from '@euler/model/bi/draft';
import { CircleCheck, CirclePlus } from 'lucide-react-native';
import { memo } from 'react';
import { Pressable } from 'react-native';
import { useBiAddTap } from './functions/useBiAddTap';
import { useOpenBiDashboard } from './functions/useOpenBiDashboard';

/**
 * Under a draft: where it was added ("已加到「X」", opening it), or
 * **加到看板** while it is on no dashboard the reader can see.
 */
export const DraftPlacement = memo(({ draft }: { draft: BiDraftDetail }) => {
  const { rpx } = useResponsiveContext();
  const attached = useAiChatAttached();
  const { pending, busy, add } = useBiAddTap([draft.id]);
  const open = useOpenBiDashboard();
  const [first, ...others] = draft.addedTo;
  if (first)
    return (
      <Pressable
        accessibilityRole="link"
        accessibilityLabel={`已加到「${first.dashboardName}」，打开看板`}
        onPress={() => open(first.dashboardId, first.dashboardName)}
        style={({ pressed }) => ({
          flexDirection: 'row',
          alignItems: 'center',
          gap: rpx(6),
          opacity: pressed ? 0.7 : 1,
        })}
      >
        <CircleCheck size={15} color={aiColors.green} />
        <Typography.Text size={rpx(12)} color={aiColors.mutedForeground}>
          已加到
        </Typography.Text>
        <Typography.Text
          medium
          size={rpx(12)}
          color={aiColors.primary}
          numberOfLines={1}
          style={{ flexShrink: 1 }}
        >
          「{first.dashboardName}」
        </Typography.Text>
        {others.length ? (
          <Typography.Text size={rpx(12)} color={aiColors.mutedForeground}>
            等 {draft.addedTo.length} 个看板
          </Typography.Text>
        ) : null}
      </Pressable>
    );
  if (!attached) return null;
  return (
    <SecondaryButton
      title="加到看板"
      icon={CirclePlus}
      loading={pending}
      disabled={busy}
      onPress={() => add()}
    />
  );
});
